import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  Stack,
  CircularProgress,
  Button,
  Alert
} from '@material-ui/core';
import Page from '../components/Page';
import GlobalAdminTable from '../components/admin/globaladmin/GlobalAdminTable';
import AddGlobalAdminDialog from '../components/admin/globaladmin/AddGlobalAdminDialog';
import useVrackApi from '../hooks/useVrackApi';

export default function AdminGlobalAdmins() {
  const { loading, error, fetchData } = useVrackApi('get', '/admin/global-admins');
  const [admins, setAdmins] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const fetchAdmins = useCallback(
    async (signal) => {
      try {
        const response = await fetchData({ signal });
        if (!signal?.aborted) {
          // API returns an array of usernames
          setAdmins(Array.isArray(response) ? response : []);
        }
      } catch (err) {
        // AbortController cancels are expected during cleanup; nothing to do.
        // Other errors are captured in the error state from useVrackApi.
        if (signal?.aborted || err?.code === 'ERR_CANCELED') {
          // no-op
        }
      }
    },
    [fetchData]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchAdmins(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = () => fetchAdmins(undefined);

  const handleAddOpenDialog = () => {
    setAddDialogOpen(true);
  };

  const handleAddCloseDialog = () => {
    setAddDialogOpen(false);
  };

  const renderContent = () => {
    if (loading && admins.length === 0) {
      return (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Stack>
      );
    }

    if (error && admins.length === 0) {
      return (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={refetch}>
              Retry
            </Button>
          }
        >
          Failed to load global admins. Please try again later.
        </Alert>
      );
    }

    return <GlobalAdminTable admins={admins} updateAdmins={refetch} />;
  };

  return (
    <Page title="Admin | Global Admins | Virtual Racks">
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
            >
              <Typography variant="h4">Global Admins</Typography>
              <Button onClick={handleAddOpenDialog} variant="contained">
                Add Global Admin
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            {renderContent()}
          </Grid>
        </Grid>
      </Container>
      <AddGlobalAdminDialog
        isOpen={addDialogOpen}
        closeHandler={handleAddCloseDialog}
        onAdded={refetch}
      />
    </Page>
  );
}
